/**
 * @nanokart/tryon-studio-sdk
 * StudioContainer — Main layout with Filters, History, Camera, Lightbox
 * (CSS animations only, no framer-motion)
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTryOnConfig } from '../../core/TryOnProvider';
import { useProviderCapabilities } from '../../core/hooks/useProviderCapabilities';
import { useInventory } from '../../core/hooks/useInventory';
import { useTryOn } from '../../core/hooks/useTryOn';
import { useMoments } from '../../core/hooks/useMoments';
import { usePhoto } from '../../core/hooks/usePhoto';
import { Header } from './Header';
import { Footer } from './Footer';
import { MainStage } from '../stage/MainStage';
import { InventoryCarousel } from '../inventory/InventoryCarousel';
import { SizeRecommendPanel } from '../size/SizeRecommendPanel';
import { CameraCaptureModal } from '../common/CameraCaptureModal';
import type { CatalogItem, TryOnResult } from '../../types/config';
import type { MomentDisplay } from '../stage/MainStage';

export interface StudioContainerProps {
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
  onEndSession?: () => void;
}

// ── Local Filter State ─────────────────────────────────────────────────────
interface LocalFilters {
  gender: '' | 'female' | 'male';
  category: string;
  search: string;
  priceMin: number;
  priceMax: number;
}

const DEFAULT_FILTERS: LocalFilters = {
  gender: '',
  category: '',
  search: '',
  priceMin: 0,
  priceMax: Infinity,
};

const PRICE_RANGES = [
  { label: 'All', min: 0, max: Infinity },
  { label: 'Under 1K', min: 0, max: 1000 },
  { label: '1K–3K', min: 1000, max: 3000 },
  { label: '3K–5K', min: 3000, max: 5000 },
  { label: '5K+', min: 5000, max: Infinity },
];

const PRO_TIPS = [
  { text: 'Just you: Single person photos work best', icon: '👤' },
  { text: 'Head to toe: Full body visible, head to feet', icon: '📏' },
  { text: 'Well-fitted clothes: Helps AI understand your shape', icon: '👕' },
  { text: 'Good lighting: Natural daylight, avoid shadows', icon: '☀️' },
  { text: 'Clear background: Remove distractions from frame', icon: '🖼️' },
];

function applyClientFilters(items: CatalogItem[], filters: LocalFilters): CatalogItem[] {
  return items.filter((item) => {
    const gender = (item.metadata?.gender as string | undefined)?.toLowerCase();
    if (filters.gender === 'female' && gender && gender !== 'female' && gender !== 'unisex') return false;
    if (filters.gender === 'male' && gender && gender !== 'male' && gender !== 'unisex') return false;
    if (filters.category && item.category?.toLowerCase() !== filters.category.toLowerCase()) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (!item.name.toLowerCase().includes(q) && !(item.category?.toLowerCase().includes(q)) && !(item.color?.toLowerCase().includes(q))) return false;
    }
    if (filters.priceMin > 0 && item.price < filters.priceMin) return false;
    if (isFinite(filters.priceMax) && item.price > filters.priceMax) return false;
    return true;
  });
}

function hasActiveFilters(f: LocalFilters): boolean {
  return !!(f.gender || f.category || f.search || f.priceMin > 0 || isFinite(f.priceMax));
}

// ── Pro Tips Modal ─────────────────────────────────────────────────────────
const ProTipsModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px', animation: 'nk-fade-in 0.2s ease-out',
    }}
    data-testid="pro-tips-modal"
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        backgroundColor: '#fff', width: '100%', maxWidth: '360px',
        borderRadius: '20px', padding: '24px',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        animation: 'nk-scale-in 0.2s ease-out',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <div style={{ width: 40, height: 40, background: '#fef3c7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: '20px' }}>💡</span>
        </div>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>Pro Tips for Best Results</h3>
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {PRO_TIPS.map((tip, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>{tip.icon}</span>
            <span style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>{tip.text}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={onClose}
        style={{ width: '100%', marginTop: '20px', padding: '12px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}
      >
        Got it
      </button>
    </div>
  </div>
);

// ── Filters Modal ──────────────────────────────────────────────────────────
interface FiltersModalProps {
  filters: LocalFilters;
  categories: string[];
  onApply: (f: LocalFilters) => void;
  onClose: () => void;
}

const FiltersModal: React.FC<FiltersModalProps> = ({ filters, categories, onApply, onClose }) => {
  const [local, setLocal] = useState<LocalFilters>(filters);
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        animation: 'nk-fade-in 0.2s ease-out',
      }}
      data-testid="filters-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#fff', width: '100%', maxWidth: '480px',
          borderRadius: '20px 20px 0 0', padding: '24px 20px',
          maxHeight: '85vh', overflowY: 'auto',
          animation: 'nk-slide-up 0.25s ease-out',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>Filters</h3>
          <button
            onClick={onClose}
            style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#f1f5f9', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            data-testid="close-filters-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Gender */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>Gender</p>
          <div style={{ display: 'flex', gap: 8, background: '#f1f5f9', borderRadius: 12, padding: 4 }}>
            {[{ value: '' as const, label: 'All' }, { value: 'female' as const, label: 'Women' }, { value: 'male' as const, label: 'Men' }].map(opt => (
              <button
                key={opt.value}
                onClick={() => setLocal(l => ({ ...l, gender: opt.value }))}
                style={{
                  flex: 1, padding: '8px 4px', borderRadius: 10, border: 'none', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 500,
                  backgroundColor: local.gender === opt.value ? '#fff' : 'transparent',
                  color: local.gender === opt.value ? (opt.value === 'female' ? '#db2777' : opt.value === 'male' ? '#2563eb' : '#1e293b') : '#64748b',
                  boxShadow: local.gender === opt.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
                data-testid={`gender-filter-${opt.value || 'all'}`}
              >{opt.label}</button>
            ))}
          </div>
        </div>

        {/* Category */}
        {categories.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>Category</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {['', ...categories].map((cat) => (
                <button
                  key={cat || '__all__'}
                  onClick={() => setLocal(l => ({ ...l, category: cat }))}
                  style={{
                    padding: '6px 14px', borderRadius: '9999px',
                    border: local.category === cat ? '2px solid #2563eb' : '2px solid #e2e8f0',
                    backgroundColor: local.category === cat ? '#eff6ff' : '#fff',
                    color: local.category === cat ? '#2563eb' : '#475569',
                    fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                  }}
                  data-testid={`category-filter-${cat || 'all'}`}
                >{cat || 'All'}</button>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>Search</p>
          <div style={{ position: 'relative' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"
              style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text" value={local.search}
              onChange={(e) => setLocal(l => ({ ...l, search: e.target.value }))}
              placeholder="Search by name or color..."
              style={{ width: '100%', paddingLeft: 36, paddingRight: 12, paddingTop: 10, paddingBottom: 10, border: '1px solid #e2e8f0', borderRadius: 12, fontSize: '13px', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}
              data-testid="filter-search-input"
            />
          </div>
        </div>

        {/* Price Range */}
        <div style={{ marginBottom: '24px' }}>
          <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>Price Range</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {PRICE_RANGES.map((range) => {
              const isActive = range.min === local.priceMin && range.max === local.priceMax;
              return (
                <button
                  key={range.label}
                  onClick={() => setLocal(l => ({ ...l, priceMin: range.min, priceMax: range.max }))}
                  style={{
                    padding: '6px 14px', borderRadius: '9999px',
                    border: isActive ? '2px solid #2563eb' : '2px solid #e2e8f0',
                    backgroundColor: isActive ? '#eff6ff' : '#fff',
                    color: isActive ? '#2563eb' : '#475569',
                    fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                  }}
                  data-testid={`price-filter-${range.label.replace(/\s+/g, '-')}`}
                >{range.label}</button>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setLocal(DEFAULT_FILTERS)}
            style={{ flex: 1, padding: '12px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}
            data-testid="reset-filters-btn">Reset</button>
          <button onClick={() => { onApply(local); onClose(); }}
            style={{ flex: 1, padding: '12px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: 12, fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
            data-testid="apply-filters-btn">Apply</button>
        </div>
      </div>
    </div>
  );
};

// ── Lightbox ───────────────────────────────────────────────────────────────
interface LightboxItem {
  url: string;
  label: string;
  type: 'photo' | 'tryon' | 'moment';
}

interface LightboxProps {
  items: LightboxItem[];
  initialIndex: number;
  onClose: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ items, initialIndex, onClose }) => {
  const [index, setIndex] = useState(Math.max(0, Math.min(initialIndex, items.length - 1)));
  const current = items[index];

  const prev = useCallback(() => setIndex(i => Math.max(0, i - 1)), []);
  const next = useCallback(() => setIndex(i => Math.min(items.length - 1, i + 1)), [items.length]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [prev, next, onClose]);

  if (!current) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.92)',
        zIndex: 9999,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        animation: 'nk-fade-in 0.2s ease-out',
      }}
      data-testid="lightbox"
    >
      {/* Close */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 20, right: 20,
          width: 40, height: 40, borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10,
        }}
        data-testid="lightbox-close-btn"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* Counter + Label */}
      <div style={{ position: 'absolute', top: 20, left: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
          {index + 1} / {items.length}
        </span>
        <span style={{
          padding: '3px 10px', borderRadius: '9999px',
          backgroundColor: current.type === 'moment' ? 'rgba(139,92,246,0.8)' : current.type === 'photo' ? 'rgba(59,130,246,0.8)' : 'rgba(16,185,129,0.8)',
          color: '#fff', fontSize: '11px', fontWeight: 600, alignSelf: 'flex-start',
        }}>
          {current.label}
        </span>
      </div>

      {/* Main image */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '90vw', maxHeight: '80vh',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <img
          key={current.url}
          src={current.url}
          alt={current.label}
          style={{
            maxWidth: '100%', maxHeight: '80vh',
            objectFit: 'contain',
            borderRadius: '8px',
            animation: 'nk-fade-in 0.2s ease-out',
          }}
          data-testid="lightbox-image"
        />
      </div>

      {/* Navigation arrows */}
      {index > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); prev(); }}
          style={{
            position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
            width: 44, height: 44, borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          data-testid="lightbox-prev-btn"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
            <polyline points="15,18 9,12 15,6" />
          </svg>
        </button>
      )}
      {index < items.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); next(); }}
          style={{
            position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
            width: 44, height: 44, borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          data-testid="lightbox-next-btn"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
            <polyline points="9,18 15,12 9,6" />
          </svg>
        </button>
      )}

      {/* Thumbnail strip */}
      {items.length > 1 && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: 24, left: 0, right: 0,
            display: 'flex', justifyContent: 'center', gap: 8,
            padding: '0 16px', overflowX: 'auto',
          }}
        >
          {items.map((item, i) => (
            <button
              key={`lb-${i}`}
              onClick={() => setIndex(i)}
              style={{
                width: 48, height: 48, flexShrink: 0,
                borderRadius: 8, overflow: 'hidden', padding: 0,
                border: i === index ? '2px solid #fff' : '2px solid rgba(255,255,255,0.2)',
                cursor: 'pointer', background: 'none',
                opacity: i === index ? 1 : 0.6,
              }}
              data-testid={`lightbox-thumb-${i}`}
            >
              <img src={item.url} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── History Strip ──────────────────────────────────────────────────────────
interface HistoryItem {
  type: 'photo' | 'tryon' | 'moment';
  imageUrl: string;
  label: string;
  productId?: string;
  result?: TryOnResult;
  momentType?: string;
}

interface HistoryStripProps {
  items: HistoryItem[];
  onSelectTryon: (result: TryOnResult) => void;
  onSelectMoment: (display: MomentDisplay) => void;
  onSelectPhoto: () => void;
  primaryColor: string;
}

const HistoryStrip: React.FC<HistoryStripProps> = ({ items, onSelectTryon, onSelectMoment, onSelectPhoto, primaryColor }) => {
  if (items.length === 0) return null;
  return (
    <div
      style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }}
      data-testid="history-strip"
    >
      {items.map((item, idx) => (
        <button
          key={`history-${item.type}-${idx}`}
          onClick={() => {
            if (item.type === 'photo') onSelectPhoto();
            else if (item.type === 'tryon' && item.result) onSelectTryon(item.result);
            else if (item.type === 'moment') onSelectMoment({ url: item.imageUrl, label: item.label });
          }}
          style={{
            flexShrink: 0, width: 52, height: 52, borderRadius: 10,
            overflow: 'hidden',
            border: `2px solid ${item.type === 'moment' ? '#8b5cf6' : item.type === 'photo' ? '#3b82f6' : primaryColor}40`,
            cursor: 'pointer', padding: 0, background: 'none', position: 'relative',
          }}
          title={item.label}
          data-testid={`history-item-${item.type}-${idx}`}
        >
          <img src={item.imageUrl} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, padding: '2px 4px',
            background: item.type === 'moment' ? 'linear-gradient(to top, rgba(139,92,246,0.85), transparent)' : item.type === 'photo' ? 'linear-gradient(to top, rgba(59,130,246,0.85), transparent)' : 'linear-gradient(to top, rgba(16,185,129,0.85), transparent)',
            display: 'flex', justifyContent: 'center',
          }}>
            <span style={{ fontSize: '7px', color: '#fff', fontWeight: 600 }}>
              {item.type === 'moment' ? 'Moment' : item.type === 'photo' ? 'You' : 'Try-on'}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
};

// ── CSS Animations ─────────────────────────────────────────────────────────
const CSS_ANIMATIONS = `
@keyframes nk-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes nk-scale-in { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
@keyframes nk-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes nk-slide-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
`;

// ── StudioContainer ────────────────────────────────────────────────────────
export const StudioContainer: React.FC<StudioContainerProps> = ({
  className, style, children, showHeader = true, showFooter = true, onEndSession,
}) => {
  const { config, features } = useTryOnConfig();
  const { forceSingleItemView, showInventoryCarousel } = useProviderCapabilities();
  const { items: allItems, selectItem, selectedItem, addCameraGarment } = useInventory();
  const { results: tryonResults, currentResult: currentTryonResult, executeTryOn } = useTryOn();
  const { results: momentResults } = useMoments();
  const { preview: photoPreview, setPhoto } = usePhoto();

  // UI state
  const [proTipsOpen, setProTipsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [dressCameraOpen, setDressCameraOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<LocalFilters>(DEFAULT_FILTERS);
  const [historyMomentDisplay, setHistoryMomentDisplay] = useState<MomentDisplay | null>(null);
  // Lightbox
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Garment fullscreen preview (fires when user taps the already-selected center card)
  const [garmentPreview, setGarmentPreview] = useState<CatalogItem | null>(null);

  // Derived: categories
  const categories = useMemo(() => {
    const cats = new Set<string>();
    allItems.forEach((item) => { if (item.category) cats.add(item.category); });
    return Array.from(cats).sort();
  }, [allItems]);

  // Derived: filtered items
  const filteredItems = useMemo(() => {
    if (!hasActiveFilters(localFilters)) return undefined;
    return applyClientFilters(allItems, localFilters);
  }, [allItems, localFilters]);

  const filterCount = useMemo(() => {
    let count = 0;
    if (localFilters.gender) count++;
    if (localFilters.category) count++;
    if (localFilters.search) count++;
    if (localFilters.priceMin > 0 || isFinite(localFilters.priceMax)) count++;
    return count;
  }, [localFilters]);

  // Derived: history items for strip + lightbox
  const historyItems = useMemo<HistoryItem[]>(() => {
    const hItems: HistoryItem[] = [];
    if (photoPreview) hItems.push({ type: 'photo', imageUrl: photoPreview, label: 'Your Photo' });
    Object.values(tryonResults).forEach((result) => {
      hItems.push({ type: 'tryon', imageUrl: result.resultImageUrl, label: 'Try-on', productId: result.productId, result });
      (momentResults[result.cacheKey] || []).forEach((m) => {
        hItems.push({ type: 'moment', imageUrl: m.resultImageUrl, label: m.momentType, productId: result.productId, momentType: m.momentType });
      });
    });
    return hItems;
  }, [photoPreview, tryonResults, momentResults]);

  // History navigation
  const handleHistoryTryOn = useCallback(async (result: TryOnResult) => {
    const matchingItem = allItems.find((i) => i.productId === result.productId);
    if (matchingItem) selectItem(matchingItem);
    await executeTryOn(result.productId);
    setHistoryMomentDisplay(null);
  }, [allItems, selectItem, executeTryOn]);

  const handleHistoryMoment = useCallback((display: MomentDisplay) => {
    setHistoryMomentDisplay(display);
  }, []);

  const handleHistoryPhoto = useCallback(() => {
    setHistoryMomentDisplay(null);
  }, []);

  // Lightbox: open from stage image click
  const handleImageClick = useCallback((imageUrl: string) => {
    const idx = historyItems.findIndex(item => item.imageUrl === imageUrl);
    setLightboxIndex(idx >= 0 ? idx : 0);
    setLightboxOpen(true);
  }, [historyItems]);

  // Camera capture — customer photo
  const handleCameraCapture = useCallback((file: File) => {
    setPhoto(file);
    setCameraOpen(false);
  }, [setPhoto]);

  // Camera capture — live dress (temporary catalog tile, not persisted)
  const handleDressCameraCapture = useCallback((file: File) => {
    addCameraGarment(file);
    setDressCameraOpen(false);
  }, [addCameraGarment]);

  // Upload dress photo from device — reuses the same "temporary catalog tile"
  // path as the live camera flow (nothing persisted to server-side inventory).
  const handleDressUpload = useCallback((file: File) => {
    addCameraGarment(file);
  }, [addCameraGarment]);

  const primaryColor = config.brandingConfig?.primaryColor ?? '#2563eb';

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    fontFamily: config.brandingConfig?.fontFamily ?? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    ...style,
  };

  if (children) {
    return (
      <div className={className} style={containerStyle} data-testid="tryon-studio-container">
        <style>{CSS_ANIMATIONS}</style>
        {showHeader ? <Header onEndSession={onEndSession} showCartBadge={features.cart} /> : null}
        {children}
        {showFooter ? <Footer /> : null}
      </div>
    );
  }

  return (
    <div className={className} style={containerStyle} data-testid="tryon-studio-container">
      <style>{CSS_ANIMATIONS}</style>

      {showHeader ? <Header onEndSession={onEndSession} showCartBadge={features.cart} showEndSession={false} /> : null}

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden' }} data-testid="studio-main">
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '8px 12px 16px', maxWidth: '640px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

          {/* Stage */}
          <MainStage
            onCameraOpen={() => setCameraOpen(true)}
            externalMomentDisplay={historyMomentDisplay}
            onInteraction={() => setHistoryMomentDisplay(null)}
            onImageClick={handleImageClick}
          />

          {/* Size Recommendation Panel — shown after a successful try-on (not for camera-captured dresses) */}
          {features.sizeRecommendation && photoPreview && selectedItem && !selectedItem.metadata?.isCameraCapture && (
            <SizeRecommendPanel productId={selectedItem.productId} />
          )}

          {/* History Strip */}
          {historyItems.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <HistoryStrip
                items={historyItems}
                onSelectTryon={handleHistoryTryOn}
                onSelectMoment={handleHistoryMoment}
                onSelectPhoto={handleHistoryPhoto}
                primaryColor={primaryColor}
              />
            </div>
          )}

          {/* Inventory Carousel */}
          {showInventoryCarousel && !forceSingleItemView ? (
            <div style={{ marginTop: '8px' }}>
              <InventoryCarousel
                overrideItems={filteredItems}
                onCameraOpen={() => setDressCameraOpen(true)}
                onUploadFile={handleDressUpload}
                onExpandSelected={(item) => setGarmentPreview(item)}
              />
            </div>
          ) : null}

          {/* Toolbar: Filter + Pro Tips */}
          <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px' }}>
            <button
              onClick={() => setFiltersOpen(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '13px', color: filterCount > 0 ? primaryColor : '#64748b',
                background: filterCount > 0 ? `${primaryColor}10` : 'transparent',
                border: filterCount > 0 ? `1px solid ${primaryColor}30` : 'none',
                padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
              }}
              data-testid="filter-button"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="11" y1="18" x2="13" y2="18" />
              </svg>
              <span>Filter</span>
              {filterCount > 0 && (
                <span
                  style={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: primaryColor, color: '#fff', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  data-testid="filter-count-badge"
                >{filterCount}</span>
              )}
            </button>

            <button
              onClick={() => setProTipsOpen(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#64748b', background: 'transparent', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer' }}
              data-testid="pro-tips-button"
            >
              <span style={{ fontSize: '14px' }}>💡</span>
              <span>Pro Tips</span>
            </button>
          </div>

          {/* Filtered items count */}
          {filteredItems !== undefined && (
            <div style={{ marginTop: 4, textAlign: 'center' }}>
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>{filteredItems.length} of {allItems.length} items</span>
              {filteredItems.length === 0 && <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#94a3b8' }}>No items match your filters</p>}
            </div>
          )}
        </div>
      </main>

      {showFooter ? <Footer /> : null}

      {/* Modals */}
      {proTipsOpen ? <ProTipsModal onClose={() => setProTipsOpen(false)} /> : null}
      {filtersOpen ? <FiltersModal filters={localFilters} categories={categories} onApply={setLocalFilters} onClose={() => setFiltersOpen(false)} /> : null}
      {cameraOpen ? <CameraCaptureModal mode="person" onCapture={handleCameraCapture} onClose={() => setCameraOpen(false)} /> : null}
      {dressCameraOpen ? <CameraCaptureModal mode="garment" onCapture={handleDressCameraCapture} onClose={() => setDressCameraOpen(false)} /> : null}
      {lightboxOpen && historyItems.length > 0 ? (
        <Lightbox
          items={historyItems.map(i => ({ url: i.imageUrl, label: i.label, type: i.type }))}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      ) : null}
      {garmentPreview ? (
        <Lightbox
          items={[{
            url: garmentPreview.imageUrl,
            label: garmentPreview.name || 'Selected dress',
            type: 'photo',
          }]}
          initialIndex={0}
          onClose={() => setGarmentPreview(null)}
        />
      ) : null}
    </div>
  );
};
